import axios from 'axios';
import { resolveApiBase } from '../api-base.js';

// Same origin in production on any host we serve, VITE_API_URL only in local dev.
const API_URL = resolveApiBase();

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Include credentials (cookies) with requests
});

// Add authentication interceptor if needed
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * API Service for interacting with the bot backend
 */
export class ApiService {
  constructor(private apiUrl: string) {}

  async get<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${this.apiUrl}${endpoint}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async post<T>(endpoint: string, data: any): Promise<T> {
    const response = await fetch(`${this.apiUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async put<T>(endpoint: string, data: any): Promise<T> {
    const response = await fetch(`${this.apiUrl}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${this.apiUrl}${endpoint}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Get bot statistics
   */
  static async getStats() {
    try {
      const response = await api.get('/api/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  }

  /**
   * Get bot statistics (alias for getStats)
   */
  static async getBotStats() {
    return this.getStats();
  }

  /**
   * Get authentication status
   */
  static async getAuthStatus() {
    try {
      const response = await fetch(`${API_URL}/api/auth/status`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching auth status:', error);
      throw error;
    }
  }

  /**
   * Create custom command
   */
  static async createCustomCommand(guildId: string, command: any) {
    try {
      const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/custom-commands`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(command)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error creating custom command for guild ${guildId}:`, error);
      throw error;
    }
  }

  /**
   * Update custom command
   */
  static async updateCustomCommand(guildId: string, commandName: string, command: any) {
    try {
      const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/custom-commands/${commandName}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(command)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating custom command ${commandName} for guild ${guildId}:`, error);
      throw error;
    }
  }

  /**
   * Delete custom command
   */
  static async deleteCustomCommand(guildId: string, commandName: string) {
    try {
      const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/custom-commands/${commandName}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error deleting custom command ${commandName} for guild ${guildId}:`, error);
      throw error;
    }
  }

  /**
   * Bash commands — saved command chains run as name.sh
   */
  static async getBashScripts(guildId: string) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/bash-scripts`, { credentials: 'include' });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }
  static async createBashScript(guildId: string, script: any) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/bash-scripts`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(script),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
    return data;
  }
  static async updateBashScript(guildId: string, name: string, script: any) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/bash-scripts/${encodeURIComponent(name)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(script),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
    return data;
  }
  static async deleteBashScript(guildId: string, name: string) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/bash-scripts/${encodeURIComponent(name)}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }
  static async saveBashConfig(guildId: string, authorRoleIds: string[]) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/bash-config`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ authorRoleIds }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
    return data;
  }
  static async getCronJobs(guildId: string) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/cron-jobs`, { credentials: 'include' });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }
  static async createCronJob(guildId: string, job: { expr: string; script: string; channelId: string }) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/cron-jobs`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(job),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
    return data;
  }
  static async deleteCronJob(guildId: string, id: number) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/cron-jobs/${id}`, {
      method: 'DELETE', credentials: 'include',
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
    return data;
  }
  static async getDailyThemes(guildId: string) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/daily-themes`, { credentials: 'include' });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }
  static async saveDailyThemeSettings(guildId: string, settings: any) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/daily-themes/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(settings),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
    return data;
  }
  static async saveCustomTheme(guildId: string, theme: any) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/daily-themes/custom`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(theme),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
    return data;
  }
  static async deleteCustomTheme(guildId: string, key: string) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/daily-themes/custom/${encodeURIComponent(key)}`, {
      method: 'DELETE', credentials: 'include',
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
    return data;
  }
  static async resolveThemeSuggestion(guildId: string, id: number, approve: boolean) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/daily-themes/suggestions/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ approve }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
    return data;
  }
  static async checkThemePosters(guildId: string, titles: string[]) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/daily-themes/poster-check?titles=${encodeURIComponent(titles.join(','))}`, { credentials: 'include' });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }
  static async postThemeNow(guildId: string) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/daily-themes/post-now`, {
      method: 'POST', credentials: 'include',
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
    return data;
  }
  static async saveCronTz(guildId: string, cronTz: string) {
    const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/cron-config`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ cronTz }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
    return data;
  }

  /**
   * Get all Discord servers the bot is in
   */
  static async getGuilds() {
    try {
      const response = await api.get('/api/guilds');
      return response.data;
    } catch (error) {
      console.error('Error fetching guilds:', error);
      throw error;
    }
  }

  /**
   * Get top users by balance
   */
  static async getTopUsers(limit = 5) {
    try {
      const response = await api.get(`/api/users/top?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching top users:', error);
      throw error;
    }
  }

  /**
   * Get recent commands executed
   */
  static async getRecentCommands(limit = 5) {
    try {
      const response = await api.get(`/api/commands/recent?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching recent commands:', error);
      throw error;
    }
  }

  /**
   * Get guild data (settings, stats, commands)
   */
  static async getGuildData(guildId: string) {
    try {
      const response = await fetch(`${API_URL}/api/bot/guild/${guildId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error fetching guild data for ${guildId}:`, error);
      throw error;
    }
  }

  /**
   * Get comprehensive guild-specific data
   */
  static async getGuildSpecificData(guildId: string) {
    try {
      const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/data`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error fetching guild-specific data for ${guildId}:`, error);
      throw error;
    }
  }

  /**
   * Get guild settings
   */
  static async getGuildSettings(guildId: string) {
    try {
      const response = await api.get(`/api/guilds/${guildId}/settings`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching settings for guild ${guildId}:`, error);
      throw error;
    }
  }

  /**
   * Update guild settings
   */
  static async updateGuildSettings(guildId: string, settings: any) {
    try {
      const response = await fetch(`${API_URL}/api/bot/guild/${guildId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating settings for guild ${guildId}:`, error);
      throw error;
    }
  }

  /**
   * Get custom commands for a guild
   */
  static async getCustomCommands(guildId: string) {
    try {
      const response = await api.get(`/api/guilds/${guildId}/commands`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching commands for guild ${guildId}:`, error);
      throw error;
    }
  }


  /**
   * Get bot configuration
   */
  static async getConfig() {
    try {
      const response = await api.get('/api/config');
      return response.data;
    } catch (error) {
      console.error('Error fetching bot config:', error);
      throw error;
    }
  }

  /**
   * Update bot configuration
   */
  static async updateConfig(config: any) {
    try {
      const response = await api.post('/api/config', config);
      return response.data;
    } catch (error) {
      console.error('Error updating bot config:', error);
      throw error;
    }
  }

  /**
   * Get all commands (built-in and custom)
   */
  static async getCommands() {
    try {
      const response = await api.get('/api/commands');
      return response.data;
    } catch (error) {
      console.error('Error fetching commands:', error);
      throw error;
    }
  }

  /**
   * Get all Minecraft map dimensions and their regions
   */
  static async getMinecraftMapDimensions() {
    try {
      const response = await api.get('/api/minecraft/map/dimensions');
      return response.data;
    } catch (error) {
      console.error('Error fetching Minecraft map dimensions:', error);
      throw error;
    }
  }

  /**
   * Get Minecraft map region data
   */
  static async getMinecraftMapRegion(dimension: string, x: number, z: number) {
    try {
      const response = await api.get(`/api/minecraft/map/region?dimension=${dimension}&x=${x}&z=${z}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching region data (${dimension} ${x}, ${z}):`, error);
      throw error;
    }
  }

  /**
   * Get approved discover commands (public)
   */
  static async getDiscoverCommands(category?: string, search?: string) {
    const params = new URLSearchParams();
    if (category && category !== 'all') params.set('category', category);
    if (search) params.set('search', search);
    const response = await fetch(`${API_URL}/api/discover/commands?${params}`, { credentials: 'include' });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Submit a command to the discover library
   */
  static async submitDiscoverCommand(data: { name: string; title: string; description: string; imageUrl?: string; category: string; tags: string[]; color?: string; footer?: string }) {
    const response = await fetch(`${API_URL}/api/discover/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Install a discover command into a server. Pass `instanceId` to target a
   * specific Luna instance's isolated DB instead of the main shared bot.
   */
  static async installDiscoverCommand(commandId: string, guildId: string, instanceId?: string) {
    const body: Record<string, string> = { guildId };
    if (instanceId) body.instanceId = instanceId;
    const response = await fetch(`${API_URL}/api/discover/commands/${commandId}/install`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Get pending discover submissions (admin only)
   */
  static async getDiscoverSubmissions() {
    const response = await fetch(`${API_URL}/api/discover/submissions`, { credentials: 'include' });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Approve a discover submission (admin only)
   */
  static async approveDiscoverSubmission(submissionId: string) {
    const response = await fetch(`${API_URL}/api/discover/submissions/${submissionId}/approve`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Reject a discover submission (admin only)
   */
  static async rejectDiscoverSubmission(submissionId: string, moderatorNote: string) {
    const response = await fetch(`${API_URL}/api/discover/submissions/${submissionId}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ moderatorNote })
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Delete a discover command from the library (admin only)
   */
  static async deleteDiscoverCommand(commandId: string) {
    const response = await fetch(`${API_URL}/api/discover/commands/${commandId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }
}

export default ApiService; 